import * as _ from 'lodash';
import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {ShippingPricelist, ShippingPricelistJSON} from './shipping-pricelist';
import {ApiResponse, AuthService, BaseService} from '@hassel-it/angular-common';

export interface ShippingPricelistsOptions {
    carrier_code?: string;
    package_type?: string;
    country_code?: string;
    is_flex?: boolean;
    is_business?: boolean;
    is_droppoint?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ShippingPricelistsService extends BaseService {

    constructor(public http: HttpClient, public authService: AuthService) {
        super(http, authService);
    }

    buildParameters(options?: ShippingPricelistsOptions): HttpParams {
        let params: HttpParams = new HttpParams();

        if (!options) {
            return params;
        }

        if (options.carrier_code) {
            params = params.set('carrier_code', options.carrier_code);
        }

        if (options.package_type) {
            params = params.set('package_type', options.package_type);
        }

        if (options.country_code) {
            params = params.set('country_code', options.country_code);
        }

        if (options.is_flex !== undefined && options.is_flex !== null) {
            params = params.set('is_flex', String(options.is_flex));
        }

        if (options.is_business !== undefined && options.is_business !== null) {
            params = params.set('is_business', String(options.is_business));
        }

        if (options.is_droppoint !== undefined && options.is_droppoint !== null) {
            params = params.set('is_droppoint', String(options.is_droppoint));
        }

        return params;
    }

    getShippingPricelists(page: number = 1, options?: ShippingPricelistsOptions): Promise<ApiResponse> {
        this.reloadHeaders();
        let params: HttpParams = this.buildParameters(options);
        params = params.set('page', <any>page);

        return this.http.get(environment.apiBase + '/api/ten-four/shipping-pricelists', {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            const results = _.chain(response.data).map((json: ShippingPricelistJSON): ShippingPricelist => {
                return ShippingPricelist.fromJSON(json);
            }).value();
            return new ApiResponse(results, response.pagination.count as number);
        }).catch(ShippingPricelistsService.handleError);
    }

    getShippingPricelist(id: string): Promise<ShippingPricelist> {
        this.reloadHeaders();

        return this.http.get(environment.apiBase + '/api/ten-four/shipping-pricelists/' + id, {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return ShippingPricelist.fromJSON(response.data);
        }).catch(ShippingPricelistsService.handleError);
    }

    createShippingPricelist(shippingPricelist: ShippingPricelist): Promise<ShippingPricelist> {
        this.reloadHeaders();

        return this.http.post(environment.apiBase + '/api/ten-four/shipping-pricelists', shippingPricelist, {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return ShippingPricelist.fromJSON(response.data);
        }).catch(ShippingPricelistsService.handleError);
    }

    addShippingPricelist(shippingPricelist: ShippingPricelist): Promise<ShippingPricelist> {
        return this.createShippingPricelist(shippingPricelist);
    }

    updateShippingPricelist(shippingPricelist: ShippingPricelist): Promise<ShippingPricelist> {
        this.reloadHeaders();

        return this.http.patch(environment.apiBase + '/api/ten-four/shipping-pricelists/' + shippingPricelist.id, shippingPricelist, {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return ShippingPricelist.fromJSON(response.data);
        }).catch(ShippingPricelistsService.handleError);
    }

    deleteShippingPricelist(shippingPricelist: ShippingPricelist | string): Promise<boolean> {
        this.reloadHeaders();
        const id = typeof shippingPricelist === 'string' ? shippingPricelist : shippingPricelist.id;

        return this.http.delete(environment.apiBase + '/api/ten-four/shipping-pricelists/' + id, {
            headers: this.httpHeaders
        }).toPromise().then(() => {
            return true;
        }).catch(ShippingPricelistsService.handleError);
    }
}
